import cn from "classnames";
import { useTranslation } from "react-i18next";
import { SCENARIO_CATEGORIES } from "src/components/ScenarioManager/useScenarioManager";
import { DropDown } from "src/components/DropDown/DropDown";
import { KendoSlider } from "src/main";
import { observer } from "mobx-react-lite";
import { useScenarioManagerWidget } from "./hooks/useScenarioManagerWidget";
import s from "./ScenarioSliders.module.scss";
import { DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";

export interface SliderValues {
  price: number;
  fx: number;
  yield: number;
  volatility: number;
}

interface ScenarioSlidersProps {
  cellId: string;
  pageId: string;
}

export const ScenarioSliders = observer(
  ({ cellId, pageId }: ScenarioSlidersProps) => {
    const { t } = useTranslation();
    const {
      selectedCategory,
      onCategoryChange,
      benchmarkName = "Benchmark",
      portfolioCurrency,
      affectedByYieldChange = true,
      affectedByVolatilityChange = true,
      onChangeSlider,
      sliderValues,
      refetchData,
    } = useScenarioManagerWidget(cellId, pageId);

    const handleCalculate = () => {
      refetchData();
    };
    const handleCategoryChange = (event: DropDownListChangeEvent) => {
      event.nativeEvent.stopImmediatePropagation();
      console.log("handleCategoryChange");
      console.log({ category: event.target.value });
      onCategoryChange(event.target.value);
    };

    return (
      <div id="scenario-sliders-container">
        <DropDown
          popupSettings={{
            popupClass: "illio-combo",
            appendTo: document.getElementById("scenario-sliders-container"),
          }}
          illioInputProps={{
            style: { marginBottom: 20 },
            className: cn(s.dropdownContainer, "illio-input"),
          }}
          className={s.dropdown}
          data={SCENARIO_CATEGORIES}
          value={selectedCategory}
          onChange={handleCategoryChange}
        />

        <p
          style={{
            backgroundColor: "var(--background-color)",
            borderRadius: "8px",
            padding: "14px",
          }}
        >
          Move the sliders to see the potential impact of various market factors
          on your Portfolio
        </p>

        <div className={s.slidersWrapper}>
          <Slider
            value={sliderValues.months}
            onChange={onChangeSlider("months")}
            label={`${t("Months from now")} ${benchmarkName}`}
            positions={[0, 3, 6, 12, 24, 36]}
            snapToPositions={true}
            labelFormat={(value) => `${value} months`}
          />
          <Slider
            value={sliderValues.price}
            onChange={onChangeSlider("price")}
            label={`${t("Move in")} ${benchmarkName}`}
            positions={[-20, -10, 0, 10, 20]}
          />
          <Slider
            value={sliderValues.fx}
            onChange={onChangeSlider("fx")}
            label={
              !!portfolioCurrency ? `${t("FX vs")} ${portfolioCurrency}` : "FX"
            }
            positions={[-10, -5, 0, 5, 10]}
          />
          <Slider
            value={sliderValues.yield}
            disabled={!affectedByYieldChange}
            onChange={onChangeSlider("yield")}
            label={t("Bond Yield")}
          />
          <Slider
            value={sliderValues.volatility}
            disabled={!affectedByVolatilityChange}
            onChange={onChangeSlider("volatility")}
            label={t("Option Volatility")}
          />
        </div>
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-evenly",
          }}
        >
          <button onClick={handleCalculate} disabled={false}>
            {t("Calculate")}
          </button>
        </div>
      </div>
    );
  }
);

const Slider = ({
  value,
  onChange,
  disabled,
  label,
  positions = [-50, -25, 0, 25, 50],
  snapToPositions = false,
  labelFormat = (value) => `${value}%`,
}: {
  value: number;
  onChange(value: number): void;
  disabled?: boolean;
  label: string;
  positions?: number[];
  snapToPositions?: boolean;
  labelFormat?: (value: number) => string;
}) => {
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <label className={cn(s.sliderLabel, "detail")}>
        {`${label} (${labelFormat(value)})`}
      </label>
      <KendoSlider
        min={positions[0]}
        max={positions[positions.length - 1]}
        disabledCondition={disabled}
        step={snapToPositions ? 1 : 0.1}
        value={value}
        defaultValue={value}
        positions={positions}
        setValueOnChange={(newValue) => {
          if (snapToPositions) {
            // Find the closest position
            const closest = positions.reduce((prev, curr) => {
              return Math.abs(curr - newValue) < Math.abs(prev - newValue)
                ? curr
                : prev;
            });
            onChange(closest);
          } else {
            onChange(newValue);
          }
        }}
      />
    </div>
  );
};
